import { Database, TreeStructure } from "@phosphor-icons/react";
import { appPaths } from "../Routes";
import { defaultScopeValue } from "../api/activity-data";
import { useEmissionsDashboard } from "../api/dashboard";
import { dataStatusIds, months } from "../constants";
import ContentContainer from "./ContentContainer";
import DateRangeFilterDropdown, {
    useDateRangeFilterDropdown,
} from "./DateRangeFilterDropdown";
import MetricCard from "./MetricCard";
import StackedBarChart from "./StackedBarChart";
import Typography from "./Typography";

const EmissionsDashboard = () => {
    const {
        value: dateRangeValue,
        label,
        handleChange,
        startDate,
        endDate,
    } = useDateRangeFilterDropdown();

    const { emissionsDashboardData, isLoading } = useEmissionsDashboard(
        startDate,
        endDate
    );

    const chartData =
        emissionsDashboardData?.carbonEmissionsChartDataCo2eKg.map((data) => ({
            label: months[data.monthIndex - 1],
            ...data,
        })) ?? [];

    if (isLoading) {
        return (
            <ContentContainer className="py-10 text-center">
                Loading...
            </ContentContainer>
        );
    }

    return (
        <div>
            <ContentContainer className="py-5">
                <DateRangeFilterDropdown
                    value={dateRangeValue}
                    onChange={handleChange}
                    selectedTextOverride={label}
                    disabledOptions={["all"]}
                />
            </ContentContainer>
            <ContentContainer className="py-8">
                <Typography variant="title-medium" component="h2">
                    Carbon emissions
                </Typography>
                <div className="flex items-end gap-4 mt-4">
                    <Typography
                        variant="display-large"
                        component="p"
                        className="text-[4rem] leading-[4rem]"
                    >
                        {emissionsDashboardData?.totalCarbonEmissionsCo2eKg.toLocaleString()}
                        kg
                    </Typography>
                    <Typography variant="headline-large" component="p">
                        CO<sub>2</sub>e
                    </Typography>
                </div>
                <StackedBarChart
                    keys={[
                        {
                            name: "Scope 1",
                            colour: "var(--scope-1)",
                        },
                        {
                            name: "Scope 2",
                            colour: "var(--scope-2)",
                        },
                        {
                            name: "Scope 3",
                            colour: "var(--scope-3)",
                        },
                    ]}
                    data={chartData}
                />
                <div className="md:grid grid-cols-2 gap-6 space-y-6 md:space-y-0">
                    <MetricCard
                        title="Activity Data Points"
                        value={emissionsDashboardData?.activityDataPointsCount}
                        icon={TreeStructure}
                        href={`${appPaths.activityData}?dateRange=${dateRangeValue}&scope=${defaultScopeValue}`}
                    />
                    <MetricCard
                        title="Emissions Sources"
                        value={emissionsDashboardData?.allEmissionsSourcesCount}
                        icon={Database}
                        href={`${appPaths.emissionsSources()}?dataStatusEs=${
                            dataStatusIds.estimated
                        }, ${dataStatusIds.measuredSecondary}, ${
                            dataStatusIds.measuredPrimary
                        }`}
                    />
                </div>
            </ContentContainer>
        </div>
    );
};

export default EmissionsDashboard;
